// Mart pick list (Partner Hub): queue ordering, the customer's substitution choice, what the
// substitution sheet may offer, barcode matching, local results over the poll and the
// "Ready for rider" gate. Pure: no DOM, no Intl. Rules come from @popcart/core/v1 (mart.js),
// never re-implemented; the server's PickResult is authoritative, this is display only.

import { effectiveSubMode, settlePick, sortPickList, substituteUnitCentavos } from '@popcart/core/v1';
import { peso } from '../../../lib/format.js';
import { readySteps } from '../../fresh/lib/weigh.js';

/** @typedef {import('@popcart/core/v1').PickOrder} PickOrder */
/** @typedef {import('@popcart/core/v1').PickLine} PickLine */
/** @typedef {import('@popcart/core/v1').PickResult} PickResult */
/** @typedef {import('@popcart/core/v1').PickResultView} PickResultView */
/** @typedef {import('@popcart/core/v1').SubstituteOption} SubstituteOption */
/** @typedef {import('@popcart/core/v1').ShopOrderStatus} ShopOrderStatus */
/** @typedef {'similar' | 'product' | 'none'} SubMode */

// ── Queue ─────────────────────────────────────────────────────────────────────────────────

/** Express first, then oldest, then code. @param {Pick<PickOrder, 'speed' | 'placedAt' | 'code'>} a @param {Pick<PickOrder, 'speed' | 'placedAt' | 'code'>} b */
function byUrgency(a, b) {
  if (a.speed !== b.speed) return a.speed === 'express' ? -1 : 1;
  if (a.placedAt !== b.placedAt) return a.placedAt < b.placedAt ? -1 : 1;
  return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
}

/**
 * The pick queue: Express first, then oldest; each order's lines in shelf order (the server sends
 * both already; sorting again keeps the screen right if it ever doesn't).
 * @param {PickOrder[]} orders
 * @returns {PickOrder[]}
 */
export function sortOrders(orders) {
  return [...orders].sort(byUrgency).map((o) => ({ ...o, lines: sortPickList(o.lines) }));
}

/**
 * Put fresher local results over the poll (a pick lands before the next refetch). A later poll
 * that already carries a newer pick (another tablet) wins.
 * @param {PickOrder[]} orders
 * @param {Record<string, PickResult>} results orderItemId → latest PickResult from this screen
 * @returns {PickOrder[]}
 */
export function applyResults(orders, results) {
  return orders.map((o) => {
    let touched = false;
    const lines = o.lines.map((l) => {
      const r = results[l.orderItemId];
      if (!r) return l;
      if (l.picked && l.picked.pickedAt > r.pickedAt) return l;
      touched = true;
      /** @type {PickResultView} */
      const picked = {
        status: r.status,
        pickedQty: r.pickedQty,
        substitute: r.substitute,
        finalLineCentavos: r.finalLineCentavos,
        deltaCentavos: r.deltaCentavos,
        pickedAt: r.pickedAt,
      };
      return { ...l, picked };
    });
    if (!touched) return o;
    // Picking an accepted order moves it to preparing (contract), so the local status follows.
    const status = o.status === 'accepted' ? 'preparing' : o.status;
    return { ...o, lines, status, allPicked: lines.every((l) => !!l.picked) };
  });
}

/** Lines still to pick. @param {Pick<PickOrder, 'lines'>} o */
export function leftToPick(o) {
  return o.lines.filter((l) => !l.picked).length;
}

/** Lines still to pick across the queue. @param {PickOrder[]} orders */
export function toPickCount(orders) {
  return orders.reduce((t, o) => t + leftToPick(o), 0);
}

/**
 * The order to show: the chosen one if it's still in the queue, else the first with something
 * left to pick, else the first.
 * @param {PickOrder[]} orders @param {string | null} selectedId
 */
export function selectOrder(orders, selectedId) {
  return orders.find((o) => o.shopOrderId === selectedId) ?? orders.find((o) => leftToPick(o) > 0) ?? orders[0] ?? null;
}

// ── The customer's substitution choice ────────────────────────────────────────────────────

/**
 * The line's effective mode. The pick list sends the effective choice; a missing one falls back
 * to the Phase 2 default (substitution allowed), the same as effectiveSubMode.
 * @param {Pick<PickLine, 'substitute'>} line
 * @returns {SubMode}
 */
export function subMode(line) {
  return effectiveSubMode(line.substitute, true);
}

/**
 * The chip on each line: "Swap similar" / "Only Pepsi 1 L" / "Don't replace".
 * @param {Pick<PickLine, 'substitute'>} line
 */
export function subLabel(line) {
  const mode = subMode(line);
  if (mode === 'none') return 'Don’t replace';
  if (mode === 'product') return `Only ${line.substitute?.productName || 'their chosen item'}`;
  return 'Swap similar';
}

/** The sheet's heading note for the mode. @param {Pick<PickLine, 'substitute'>} line */
export function subRule(line) {
  const mode = subMode(line);
  if (mode === 'none') return 'The customer asked not to replace this. Leave it out and they get the money back.';
  if (mode === 'product') return `The customer will only take ${line.substitute?.productName || 'the item they chose'}. Use it, or leave it out.`;
  return 'The customer is happy with something similar. They never pay more than the original price.';
}

/**
 * What the sheet may offer, restricted by the customer's mode:
 *  · none → nothing (only "Leave it out");
 *  · product → only that product;
 *  · similar → the server's options (never the item itself).
 * Sold-out options are dropped.
 * @param {Pick<PickLine, 'substitute' | 'productId'>} line
 * @param {SubstituteOption[]} options
 * @returns {SubstituteOption[]}
 */
export function allowedSubstitutes(line, options) {
  const mode = subMode(line);
  if (mode === 'none') return [];
  const ok = options.filter((o) => o.available > 0 && o.productId !== line.productId);
  if (mode === 'product') return ok.filter((o) => o.productId === line.substitute?.productId).slice(0, 1);
  return ok;
}

/**
 * For mode 'product' when the chosen item isn't among /substitutes (it may be another category):
 * build the option from the shop's own product record.
 * @param {{ id: string; name: string; priceCentavos: number; available: number; variantLabel?: string | null; shelf?: string | null }} p
 * @param {number} originalUnitCentavos
 * @returns {SubstituteOption}
 */
export function optionFromProduct(p, originalUnitCentavos) {
  return {
    productId: p.id,
    name: p.name,
    variantLabel: p.variantLabel ?? null,
    priceCentavos: p.priceCentavos,
    available: p.available,
    shelf: p.shelf ?? null,
    chargedUnitCentavos: substituteUnitCentavos(originalUnitCentavos, p.priceCentavos),
  };
}

/** "Pepsi 1 L" from a name and variant label. @param {{ name: string; variantLabel?: string | null }} x */
export function fullName(x) {
  return x.variantLabel ? `${x.name} ${x.variantLabel}` : x.name;
}

/**
 * What the customer pays per substitute unit, as shown on the option. The server's
 * chargedUnitCentavos is used; it is the lower of the two prices.
 * @param {SubstituteOption} o @param {number} originalUnitCentavos
 * @returns {{ text: string; capped: boolean; chargedUnitCentavos: number }}
 */
export function chargedText(o, originalUnitCentavos) {
  const charged = Number.isFinite(o.chargedUnitCentavos) ? o.chargedUnitCentavos : substituteUnitCentavos(originalUnitCentavos, o.priceCentavos);
  const capped = o.priceCentavos > charged;
  return {
    chargedUnitCentavos: charged,
    capped,
    text: capped ? `Customer pays ${peso(charged)} each (shelf price ${peso(o.priceCentavos)})` : `Customer pays ${peso(charged)} each`,
  };
}

/** How many are missing after picking `pickedQty`. @param {Pick<PickLine, 'qty'>} line @param {number} pickedQty */
export function missingQty(line, pickedQty) {
  return Math.max(0, line.qty - clampQty(pickedQty, line.qty));
}

/** Clamp a stepper count into 0..max. @param {number} n @param {number} max */
export function clampQty(n, max) {
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(0, Math.trunc(n)), Math.max(0, max));
}

/**
 * Preview the line before saving (the shared settlePick maths).
 * @param {Pick<PickLine, 'qty' | 'unitCentavos'>} line @param {number} pickedQty
 * @param {{ priceCentavos: number; qty: number } | null} [sub]
 */
export function previewPick(line, pickedQty, sub = null) {
  const picked = clampQty(pickedQty, line.qty);
  const missing = line.qty - picked;
  const r = settlePick({
    unitCentavos: line.unitCentavos,
    qty: line.qty,
    pickedQty: picked,
    substitute: sub && missing > 0 ? { unitCentavos: sub.priceCentavos, qty: clampQty(sub.qty, missing) } : null,
  });
  return { ...r, text: r.deltaCentavos < 0 ? `Line ${peso(r.finalLineCentavos)} · ${peso(-r.deltaCentavos)} back to the customer` : `Line ${peso(r.finalLineCentavos)}` };
}

/**
 * The PickInput for the sheet's choice.
 * @param {number} pickedQty @param {{ productId: string; qty: number } | null} sub
 * @returns {import('@popcart/core/v1').PickInput}
 */
export function pickInput(pickedQty, sub) {
  if (!sub || sub.qty < 1) return { pickedQty, substitute: null };
  return { pickedQty, substitute: { productId: sub.productId, qty: sub.qty } };
}

/** The line's result in a few words. @param {PickLine} l */
export function resultLabel(l) {
  const p = l.picked;
  if (!p) return null;
  if (p.status === 'picked') return `Picked ${p.pickedQty} of ${l.qty}`;
  if (p.status === 'substituted' && p.substitute) return `${p.pickedQty} of ${l.qty} found · ${p.substitute.qty} × ${p.substitute.name}`;
  return p.pickedQty ? `${p.pickedQty} of ${l.qty} found · rest left out` : 'Left out';
}

/** What to announce after a pick. @param {PickLine} l @param {PickResult} r */
export function savedMessage(l, r) {
  const parts = [`${fullName(l)}: `];
  if (r.status === 'picked') parts[0] += `all ${r.pickedQty} picked`;
  else if (r.status === 'substituted' && r.substitute) parts[0] += `${r.pickedQty} of ${l.qty} found, ${r.substitute.qty} × ${r.substitute.name} instead`;
  else parts[0] += `${r.pickedQty} of ${l.qty} found`;
  if (r.deltaCentavos < 0) parts.push(`${peso(-r.deltaCentavos)} less`);
  const s = settlementText(r);
  if (s) parts.push(s);
  return parts.join(' · ') + '.';
}

/**
 * The order's new money once every Mart line is picked (null before that).
 * @param {Pick<PickResult, 'allPicked' | 'orderFinalTotalCentavos' | 'codDueCentavos' | 'refundCentavos'>} r
 */
export function settlementText(r) {
  if (!r.allPicked) return null;
  const parts = ['Every Grocery item picked'];
  if (r.orderFinalTotalCentavos !== null && r.orderFinalTotalCentavos !== undefined) parts.push(`order total ${peso(r.orderFinalTotalCentavos)}`);
  if (r.codDueCentavos !== null && r.codDueCentavos !== undefined) parts.push(`COD due is now ${peso(r.codDueCentavos)}`);
  else if (r.refundCentavos) parts.push(`${peso(r.refundCentavos)} goes back to the customer`);
  return parts.join(' · ');
}

// ── Barcode ───────────────────────────────────────────────────────────────────────────────

/** A scanned or typed code: spaces dropped, upper-cased. @param {string} s */
export function normalizeCode(s) {
  return String(s ?? '').replace(/\s+/g, '').toUpperCase();
}

/**
 * Apply a scan to the open order: +1 on the first matching line that still has room (shelf order).
 *  · tick: counted (count is the new found count; complete when it reached qty);
 *  · full: every matching line is already complete;
 *  · none: no line has this code (look it up to say what it is).
 * Lines already saved count from their picked qty; unsaved counts come from `counts`.
 * @param {PickLine[]} lines
 * @param {Record<string, number>} counts orderItemId → found count on screen (unsaved)
 * @param {string} raw
 * @returns {{ kind: 'tick'; orderItemId: string; count: number; complete: boolean } | { kind: 'full'; orderItemId: string } | { kind: 'none'; code: string } | { kind: 'empty' }}
 */
export function applyScan(lines, counts, raw) {
  const code = normalizeCode(raw);
  if (!code) return { kind: 'empty' };
  const matches = lines.filter((l) => l.barcode && normalizeCode(l.barcode) === code);
  if (!matches.length) return { kind: 'none', code };
  for (const l of matches) {
    const now = foundCount(l, counts);
    if (now < l.qty) return { kind: 'tick', orderItemId: l.orderItemId, count: now + 1, complete: now + 1 === l.qty };
  }
  return { kind: 'full', orderItemId: /** @type {PickLine} */ (matches[0]).orderItemId };
}

/**
 * A line found by productId (the server's barcode lookup), for codes the line doesn't carry.
 * @param {PickLine[]} lines @param {string} productId
 */
export function lineForProduct(lines, productId) {
  return lines.find((l) => l.productId === productId) ?? null;
}

/**
 * The found count the stepper shows: what's on screen, else the saved pick, else 0.
 * @param {PickLine} l @param {Record<string, number>} counts
 */
export function foundCount(l, counts) {
  const c = counts[l.orderItemId];
  if (typeof c === 'number') return clampQty(c, l.qty);
  return l.picked ? l.picked.pickedQty : 0;
}

// ── Ready for rider ───────────────────────────────────────────────────────────────────────

/** Every Mart line has a result. @param {Pick<PickOrder, 'lines'>} o */
export function isAllPicked(o) {
  return o.lines.length > 0 && o.lines.every((l) => !!l.picked);
}

/**
 * The order's footer: the Volt "Ready for rider" once every Mart line is resolved, a count of
 * what's left, or done.
 * @param {Pick<PickOrder, 'lines' | 'status'>} o
 * @returns {{ kind: 'ready'; steps: ('accepted' | 'preparing' | 'ready')[] } | { kind: 'pick'; left: number } | { kind: 'done' } | { kind: 'unknown' }}
 */
export function orderAction(o) {
  if (o.status === 'ready' || o.status === 'picked_up' || o.status === 'delivered') return { kind: 'done' };
  if (!isAllPicked(o)) return { kind: 'pick', left: leftToPick(o) };
  const steps = readySteps(o.status);
  return steps ? { kind: 'ready', steps } : { kind: 'unknown' };
}

/** Picking is allowed until the order is ready. @param {Pick<PickOrder, 'status'>} o */
export function isLocked(o) {
  return o.status === 'ready' || o.status === 'picked_up' || o.status === 'delivered' || o.status === 'cancelled';
}

/** The 409 for advancing with unpicked Mart lines ("Pick every Grocery item first."). @param {unknown} err */
export function isUnpickedConflict(err) {
  const e = /** @type {{ status?: number; message?: string } | null} */ (err);
  return !!e && e.status === 409 && /\bpick/i.test(e.message || '');
}

/** The 409 for advancing with unweighed Fresh lines (mixed orders). @param {unknown} err */
export function isUnweighedConflict(err) {
  const e = /** @type {{ status?: number; message?: string } | null} */ (err);
  return !!e && e.status === 409 && /weigh/i.test(e.message || '');
}

/** "Fresh and Grill" for the other lines on the order. @param {import('@popcart/core/v1').ProductLine[]} lines */
export function otherLinesText(lines) {
  const names = lines.filter((l) => l !== 'mart').map((l) => (l === 'fresh' ? 'Fresh' : 'Grill'));
  return [...new Set(names)].join(' and ');
}
